import React, { useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { companyInfo } from '../mock';

const TALLY_EMBED_URL = "https://tally.so/embed/2ERWMD?hideTitle=1&transparentBackground=1&dynamicHeight=1";

const Contact = () => {
  // Load Tally widget script and trigger height auto-resize after mount
  useEffect(() => {
    const SCRIPT_ID = 'tally-embed-script';
    const init = () => {
      if (window.Tally && typeof window.Tally.loadEmbeds === 'function') {
        window.Tally.loadEmbeds();
      }
    };
    if (document.getElementById(SCRIPT_ID)) {
      init();
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://tally.so/widgets/embed.js';
    s.async = true;
    s.onload = init;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow" style={{ color: '#f4a8b4' }}>Contact Us</span>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold mb-5 mt-3 text-balance">Get in Touch</h1>
            <p className="text-base md:text-lg text-slate-300">
              Have questions about our products or services? Our team of experts is here to help you find the right fire and safety solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Corporate Office</h3>
                      <a
                        href="https://www.google.com/maps/search/?api=1&query=Bhartiya+Vehicles+and+Engineering+Co.+Ltd%2C+A-29%2C+Industrial+Area%2C+Site-IV%2C+Sahibabad%2C+Ghaziabad%2C+Uttar+Pradesh+201010"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-red-600 underline underline-offset-2 hover:text-red-700 transition-colors"
                        data-testid="corporate-address-link"
                      >
                        {companyInfo.address}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">WhatsApp</h3>
                      <a
                        href="https://wa.me/919205262380"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-red-600 underline underline-offset-2 hover:text-red-700 transition-colors"
                        data-testid="whatsapp-link"
                      >
                        +91 92052 62380
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Phone</h3>
                      <a href={`tel:${companyInfo.phone}`} className="text-sm text-slate-600 hover:text-red-600">
                        {companyInfo.phone}
                      </a>
                      <p className="text-sm text-slate-600 mt-1">+91-120-2895733</p>
                      <p className="text-sm text-slate-600">+91-120-2896063</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Email</h3>
                      <a href={`mailto:${companyInfo.email}`} className="text-sm text-red-600 underline underline-offset-2 hover:text-red-700 break-all transition-colors">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Business Hours</h3>
                      <p className="text-sm text-slate-600">Monday - Saturday: 9:00 AM - 5:30 PM</p>
                      <p className="text-sm text-slate-600">Sunday: Closed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form — powered by Tally */}
            <div className="lg:col-span-2">
              <Card className="border border-slate-200 shadow-none rounded-md">
                <CardContent className="p-6 md:p-10">
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-2">Send us a Message</h2>
                  <p className="text-sm text-slate-500 mb-6">Fill in the form below and our team will respond shortly.</p>
                  <iframe
                    data-tally-src={TALLY_EMBED_URL}
                    loading="lazy"
                    width="100%"
                    height="500"
                    frameBorder="0"
                    marginHeight="0"
                    marginWidth="0"
                    title="Bhartiya Vehicles Contact Form"
                    data-testid="contact-form-iframe"
                  ></iframe>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-lg text-slate-600">Located in Sahibabad Industrial Area, Ghaziabad</p>
          </div>
          <div className="rounded-lg overflow-hidden shadow-lg max-w-3xl mx-auto" style={{ height: '280px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3500.7!2d77.3585!3d28.6817!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sBhartiya%20Vehicles%20and%20Engineering%20Co.%20Ltd!5e0!3m2!1sen!2sin!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Bhartiya Vehicles & Engineering Co. Ltd. Location"
            ></iframe>
          </div>
          <div className="text-center mt-6">
            <a 
              href="https://maps.app.goo.gl/rk6aE7A24gGATgjq7?g_st=ic" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
            >
              <MapPin className="h-5 w-5" />
              <span>Open in Google Maps</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;